import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { getItem, createItem, updateItem, listSuppliers, listCategories, Supplier, Category } from './api';
import './inventory-v2.css';

const BASE_UNITS = ['gram', 'kg', 'ml', 'litre', 'count', 'bag', 'can', 'case', 'box', 'pack', 'lid', 'cup'];

const ItemForm: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const { sessionToken } = useAuth();
  const navigate = useNavigate();
  const isEdit = Boolean(id);

  const [suppliers, setSuppliers] = useState<Supplier[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const [form, setForm] = useState({
    name: '',
    supplier_id: '',
    brand: '',
    category_id: '',
    base_unit: 'count',
    purchase_unit: '',
    purchase_quantity: '',
    purchase_cost: '',
    on_hand_quantity: '0',
    threshold_quantity: '',
    par_level: '',
    notes: '',
    price_per_unit_override: '',
    sku: '',
    supplier_sku: '',
  });

  useEffect(() => {
    const loadData = async () => {
      try {
        const [suppRes, catRes] = await Promise.all([
          listSuppliers(sessionToken),
          listCategories(sessionToken),
        ]);
        setSuppliers(suppRes.suppliers);
        setCategories(catRes.categories);

        if (isEdit && id) {
          const { item } = await getItem(sessionToken, parseInt(id));
          setForm({
            name: item.name,
            supplier_id: item.supplier_id ? String(item.supplier_id) : '',
            brand: item.brand || '',
            category_id: item.category_id ? String(item.category_id) : '',
            base_unit: item.base_unit,
            purchase_unit: item.purchase_unit || '',
            purchase_quantity: item.purchase_quantity != null ? String(item.purchase_quantity) : '',
            purchase_cost: item.purchase_cost != null ? String(item.purchase_cost) : '',
            on_hand_quantity: String(item.on_hand_quantity),
            threshold_quantity: item.threshold_quantity != null ? String(item.threshold_quantity) : '',
            par_level: item.par_level != null ? String(item.par_level) : '',
            notes: item.notes || '',
            price_per_unit_override: item.price_manually_overridden && item.last_received_unit_price != null ? String(item.last_received_unit_price) : '',
            sku: item.sku || '',
            supplier_sku: item.supplier_sku || '',
          });
        }
      } catch (e: any) {
        setError(e.message);
      } finally {
        setLoading(false);
      }
    };
    loadData();
  }, [sessionToken, id, isEdit]);

  const calculatedUnitPrice = form.purchase_cost && form.purchase_quantity
    ? (Number(form.purchase_cost) / Number(form.purchase_quantity)).toFixed(4)
    : null;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSaving(true);

    try {
      const body: any = {
        name: form.name,
        supplier_id: form.supplier_id ? Number(form.supplier_id) : null,
        brand: form.brand || null,
        category_id: form.category_id ? Number(form.category_id) : null,
        base_unit: form.base_unit,
        purchase_unit: form.purchase_unit || null,
        purchase_quantity: form.purchase_quantity ? Number(form.purchase_quantity) : null,
        purchase_cost: form.purchase_cost ? Number(form.purchase_cost) : null,
        ...(!id ? {on_hand_quantity: Number(form.on_hand_quantity) || 0} : {}),
        threshold_quantity: form.threshold_quantity ? Number(form.threshold_quantity) : null,
        par_level: form.par_level ? Number(form.par_level) : null,
        notes: form.notes || null,
        sku: form.sku || null,
        supplier_sku: form.supplier_sku || null,
      };

      if (form.price_per_unit_override) {
        body.price_per_unit_override = Number(form.price_per_unit_override);
      }

      if (isEdit && id) {
        await updateItem(sessionToken, parseInt(id), body);
        navigate(`/inventory/items/${id}`);
      } else {
        const { item } = await createItem(sessionToken, body);
        navigate(`/inventory/items/${item.id}`);
      }
    } catch (e: any) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  };

  const onChange = (field: string) => (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    setForm(prev => ({ ...prev, [field]: e.target.value }));
  };

  if (loading) return <div className="inv2-loading">Loading...</div>;

  return (
    <div className="inv2-page">
      <div className="inv2-header">
        <div>
          <Link to="/inventory/items" className="inv2-back-link">← Back to Items</Link>
          <h1>{isEdit ? 'Edit Item' : 'Add New Item'}</h1>
        </div>
      </div>

      {error && <div className="inv2-error">{error}</div>}

      <form onSubmit={handleSubmit} className="inv2-form">
        <div className="inv2-form-grid">
          <div className="inv2-form-group">
            <label>Item Name *</label>
            <input type="text" value={form.name} onChange={onChange('name')} required className="inv2-input" />
          </div>

          <div className="inv2-form-group">
            <label>Supplier</label>
            <select value={form.supplier_id} onChange={onChange('supplier_id')} className="inv2-select">
              <option value="">— Select supplier —</option>
              {suppliers.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
            </select>
          </div>

          <div className="inv2-form-group">
            <label>Brand</label>
            <input type="text" value={form.brand} onChange={onChange('brand')} className="inv2-input" />
          </div>

          <div className="inv2-form-group">
            <label>Category</label>
            <select value={form.category_id} onChange={onChange('category_id')} className="inv2-select">
              <option value="">— Select category —</option>
              {categories.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          </div>

          <div className="inv2-form-group">
            <label>Base Unit *</label>
            <select value={form.base_unit} onChange={onChange('base_unit')} required className="inv2-select">
              {BASE_UNITS.map(u => <option key={u} value={u}>{u}</option>)}
            </select>
          </div>

          <div className="inv2-form-group">
            <label>Purchase Unit</label>
            <input type="text" value={form.purchase_unit} onChange={onChange('purchase_unit')} placeholder="e.g. 16 Litre crate" className="inv2-input" />
          </div>

          <div className="inv2-form-group">
            <label>Purchase Quantity (in base units)</label>
            <input type="number" step="any" value={form.purchase_quantity} onChange={onChange('purchase_quantity')} className="inv2-input" />
          </div>

          <div className="inv2-form-group">
            <label>Purchase Cost ($)</label>
            <input type="number" step="0.01" value={form.purchase_cost} onChange={onChange('purchase_cost')} className="inv2-input" />
          </div>

          {calculatedUnitPrice && (
            <div className="inv2-form-group inv2-calculated">
              <label>Calculated Price per Unit</label>
              <div className="inv2-calculated-value">${calculatedUnitPrice} / {form.base_unit}</div>
            </div>
          )}

          <div className="inv2-form-group">
            <label>Override Price per Unit ($)</label>
            <input type="number" step="0.0001" value={form.price_per_unit_override} onChange={onChange('price_per_unit_override')} placeholder="Leave blank to use calculated" className="inv2-input" />
            {form.price_per_unit_override && <span className="inv2-hint">⚠️ Manual override active</span>}
          </div>

          <div className="inv2-form-group">
            <label>On-Hand Stock ({form.base_unit})</label>
            <input disabled={!!id} type="number" step="any" value={form.on_hand_quantity} onChange={onChange('on_hand_quantity')} className="inv2-input" />
            {id && <span className="inv2-hint">Record current stock in <Link to="/inventory">Inventory → Stock counts</Link>.</span>}
          </div>

          <div className="inv2-form-group">
            <label>Low Stock Threshold</label>
            <input type="number" step="any" value={form.threshold_quantity} onChange={onChange('threshold_quantity')} placeholder="Alert when stock falls below" className="inv2-input" />
          </div>

          <div className="inv2-form-group">
            <label>Par Level (Target Stock)</label>
            <input type="number" step="any" value={form.par_level} onChange={onChange('par_level')} placeholder="Target stock to maintain" className="inv2-input" />
          </div>

          <div className="inv2-form-group inv2-form-full">
            <label>Notes</label>
            <textarea value={form.notes} onChange={onChange('notes')} rows={3} className="inv2-textarea" />
          </div>

          <div className="inv2-form-group">
            <label>Internal SKU</label>
            <input type="text" value={form.sku} onChange={onChange('sku')} className="inv2-input" placeholder="e.g. MILK-3.25-4L" />
            <span className="inv2-hint">Your internal item code (optional)</span>
          </div>
          <div className="inv2-form-group">
            <label>Supplier SKU / Product Code</label>
            <input type="text" value={form.supplier_sku} onChange={onChange('supplier_sku')} className="inv2-input" placeholder="e.g. GS-1234" />
            <span className="inv2-hint">Supplier's product code — used for invoice matching</span>
          </div>
        </div>

        <div className="inv2-form-actions">
          <button type="submit" disabled={saving} className="inv2-btn inv2-btn-primary">
            {saving ? 'Saving...' : isEdit ? 'Update Item' : 'Create Item'}
          </button>
          <Link to="/inventory/items" className="inv2-btn">Cancel</Link>
        </div>
      </form>
    </div>
  );
};

export default ItemForm;
